"use client";

import Link from "next/link";
import Image from "next/image";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  MessageSquare, BarChart3, BrainCircuit,
  ArrowRight, CheckCircle2, Layers, Zap,
  Target, TrendingDown, TrendingUp, Clock, AlertTriangle,
  Bot, Shield, ShieldAlert, KeyRound, Sparkles, FlaskConical, X,
  ChevronRight, Database, Search, Activity, Check,
  Download, Gauge, Briefcase, LineChart, Building2,
} from "lucide-react";
import { APP_VERSION } from "@/lib/version";
// Beta 04.06.x (11/06/2026): tracking GA4/GTM/HubSpot via dataLayer
import {
  trackCtaClick, trackNavClick, trackOnepagerDownload,
  trackFormSubmit, trackFormError, trackFormStart, trackFormFieldInteract,
} from "@/lib/analytics/dataLayer";
import { LandingTracker } from "@/components/analytics/landing-tracker";
import { LandingV2Blocks } from "@/components/landing/landing-v2-blocks";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import { ArchitectureDiagram } from "@/components/landing/architecture-diagram";

// URL do one-pager com cache buster baseado na versão.
// Cada bump de version.ts invalida o cache do CDN e do browser.
const ONE_PAGER_URL = `/ser-ai-insight-one-pager.pdf?v=${encodeURIComponent(APP_VERSION)}`;

const AUNICA_LOGO = "https://www.aunica.com/wp-content/uploads/2020/09/marca_final_analitica_degrade_A_branco.png";
const AUNICALABS_LOGO = "https://www.aunica.com/wp-content/uploads/2025/08/aunica_labs_para_fudo_preto.png";

// Beta 04.06.001 (10/06/2026, Ricardo P0 site restyle):
// fontes agora agrupadas por categoria pro chip cloud não virar bagunça.
// Removido duplicado "Search Console". Removido "GTM" duplicado (Google Tag Manager).
const SOURCE_GROUPS: Array<{
  category: string;
  sources: Array<{ name: string; available: boolean }>;
}> = [
  {
    category: 'Mídia',
    sources: [
      { name: "Google Ads", available: true },
      { name: "Meta Ads", available: true },
      { name: "TikTok Ads", available: true },
      { name: "LinkedIn Ads", available: true },
      { name: "Amazon Ads", available: false },
      { name: "Pinterest Ads", available: false },
    ],
  },
  {
    category: 'Analytics',
    sources: [
      { name: "GA4", available: true },
      { name: "Adobe Analytics", available: true },
      { name: "Search Console", available: true },
      { name: "Google Tag Manager", available: true },
      { name: "Amplitude", available: true },
    ],
  },
  {
    category: 'Data & Planilhas',
    sources: [
      { name: "BigQuery", available: true },
      { name: "Google Sheets (sync 1h)", available: true },
      { name: "OneDrive / Excel Online", available: true },
      { name: "Sharepoint Excel", available: true },
      { name: "Upload XLSX/CSV", available: true },
      { name: "SFTP", available: true },
    ],
  },
  {
    category: 'E-commerce & CRM',
    sources: [
      { name: "Mercado Livre", available: true },
      { name: "Salesforce", available: true },
      { name: "HubSpot", available: true },
      { name: "MoEngage", available: true },
      { name: "Insider", available: true },
      { name: "RD Station", available: false },
      { name: "VTEX", available: false },
      { name: "Shopify", available: false },
    ],
  },
];

// Pricing intencional: nada de preços nem módulos contratáveis fechados.
// Modelo é "fale com a gente, montamos o setup do seu time".
// Beta 04.06.002 (10/06/2026, Ricardo P0): paleta violeta uniforme com
// nuances ao inves de rainbow azul/violeta/cyan.
const CONTACT_TRACKS = [
  {
    name: "Conhecer a plataforma",
    description: "Quer entender o que o Seraik Insights faz, ver os módulos rodando e como conecta nas suas fontes.",
    icon: MessageSquare,
    accent: "text-violet-300",
    border: "border-violet-500/30 bg-violet-500/5",
  },
  {
    name: "Operação para o time",
    description: "Sua equipe quer começar a usar o Squad de Analistas, conectar fontes e operar com Painel + Mesas.",
    icon: Layers,
    accent: "text-violet-200",
    border: "border-violet-500/40 bg-violet-500/8",
  },
  {
    name: "Customizado / On-Premise",
    description: "Volume alto, regulação (BACEN, SUSEP, LGPD), deploy privado, integração com sua stack interna.",
    icon: Shield,
    accent: "text-indigo-300",
    border: "border-indigo-500/30 bg-indigo-500/5",
  },
];

// Squad completo — 4 ativos + 7 em breve. Fonte: docs/COLABORADORES-SQUAD.md
const SQUAD_ACTIVE = [
  {
    name: "Mayra",
    role: "Analista de Negócios & Briefing",
    seniority: "Pleno",
    description: "Respostas instantâneas para KPIs do dia a dia. Direta, objetiva, sem rodeios. Coloca o número em destaque e contextualiza para o negócio.",
    triggers: ["Qual meu ROAS hoje?", "Sessões da semana", "Resumo executivo"],
  },
  {
    name: "Sofia",
    role: "Analista de Dados & Tendências",
    seniority: "Pleno",
    description: "Séries temporais, funil de conversão, sazonalidade, cohorts. Olho clínico para outliers e visão histórica de comportamento.",
    triggers: ["Análise de funil", "Comparar períodos", "Cohort de retenção"],
  },
  {
    name: "José",
    role: "Consultor de Performance Digital",
    seniority: "Sênior",
    description: "Google Ads, Meta, TikTok, LinkedIn, zero silos. Foco em ROI: situação, diagnóstico e recomendação, sem enrolação.",
    triggers: ["ROAS por campanha", "CPL Meta", "Qual canal pausar?"],
  },
  {
    name: "Lucas",
    role: "Consultor MarTech & Dados",
    seniority: "Especialista",
    description: "Stack MarTech, GTM, CDP, atribuição, tracking. Diagnóstico técnico cruzando múltiplas fontes em raciocínio integrado.",
    triggers: ["Por que a conversão caiu?", "Stack está completa?", "Qual CDP faz sentido?"],
  },
  {
    name: "Elena",
    role: "Analista Adobe Analytics",
    seniority: "Sênior",
    description: "Vocabulário Adobe nativo: eVars, sProps, events, segments, calculated metrics e Workspace projects. Atende clientes enterprise: aviação, bancos, varejo top.",
    triggers: ["Qual eVar converte mais?", "Análise de Workspace project", "Comparar segments"],
  },
  // Beta 04.06.004 (10/06/2026, Ricardo P0): 'temos mais de 5 analistas ativos
  // na squad'. Banco squad_analysts status=active retorna 12 nomes. Vou
  // mostrar todos como ativos, removendo COMING (so Diego sobra em breve).
  {
    name: "Rafael",
    role: "Especialista em Mídia Programática",
    seniority: "Sênior",
    description: "DV360, The Trade Desk, RTB, viewability, brand safety, CTV, DOOH. Mídia programática end-to-end.",
    triggers: ["DV360 vs Trade Desk", "Viewability acima de 70%", "CTV mix"],
  },
  {
    name: "Beatriz",
    role: "CRO & Experiência do Usuário",
    seniority: "Sênior",
    description: "Hipóteses de otimização, A/B test, heatmaps, jornada e friction points. Combina dados quanti + quali.",
    triggers: ["Onde estão os friction points?", "Hipótese A/B test", "CRO no checkout"],
  },
  {
    name: "Camila",
    role: "SEO & Estratégia de Conteúdo",
    seniority: "Pleno",
    description: "Search Console, keyword research, Core Web Vitals, calendário editorial. Orgânico de longo prazo.",
    triggers: ["Top keywords caindo", "Core Web Vitals", "Plano editorial"],
  },
  {
    name: "Fernanda",
    role: "CRM & Retenção de Clientes",
    seniority: "Pleno",
    description: "Lifecycle, churn, LTV, RFM, réguas de e-mail/SMS/WhatsApp/push. Estratégia de retenção.",
    triggers: ["LTV cohort", "Régua de reativação", "Churn por segmento"],
  },
  {
    name: "André",
    role: "Analytics Engineering, BI & CDP",
    seniority: "Especialista",
    description: "Queries SQL, BigQuery, dbt, Looker, modelagem de schemas e pipelines. Tradução técnica → executiva.",
    triggers: ["Schema mais limpo", "Modelagem dbt", "Pipeline lake"],
  },
  {
    name: "Maria",
    role: "Cientista de Dados & Modelagem",
    seniority: "Especialista",
    description: "MMM, atribuição estatística, regressão, séries temporais, forecast. Profundidade matemática com leitura executiva.",
    triggers: ["Forecast próximos 30d", "MMM saturação", "Estatística do modelo"],
  },
  {
    name: "Pedro",
    role: "Customer Success & Onboarding",
    seniority: "Pleno",
    description: "Onboarding técnico, primeiros 30 dias, configuração de workspace, treino do time. Conexão entre operação e Squad.",
    triggers: ["Como conectar Adobe?", "Setup inicial", "Treino da equipe"],
  },
  {
    name: "Igor",
    role: "Atribuição & Modelos de Conversão",
    seniority: "Especialista",
    description: "MTA, last-click, data-driven, journey analytics. Distribui crédito entre canais com rigor metodológico.",
    triggers: ["MTA vs last-click", "Co-assist Meta+Google", "Atribuição multi-touch"],
  },
];

const SQUAD_COMING = [
  { name: "Diego",    role: "E-commerce & Marketplaces", seniority: "Sênior", desc: "Mercado Livre, VTEX, Shopify: catálogo, pricing, fulfillment, reputação." },
  { name: "Bruno",    role: "Inteligência de Mercado",   seniority: "Especialista", desc: "Benchmarks de setor, share of voice, dados de mercado externos via Perplexity." },
];

const COMPARISONS = [
  { feature: "Painel Executivo pronto por segmento", insights: true, bi: false, llm: false, analytics_ai: false },
  { feature: "Responde em português com contexto de negócio", insights: true, bi: false, llm: "parcial", analytics_ai: false },
  { feature: "Conectado aos seus dados reais (GA4, Ads, ML...)", insights: true, bi: "setup complexo", llm: false, analytics_ai: "limitado" },
  { feature: "Integra múltiplas fontes em uma análise só", insights: true, bi: true, llm: false, analytics_ai: "parcial" },
  { feature: "Proativo: alerta sem você perguntar", insights: true, bi: false, llm: false, analytics_ai: "parcial" },
  { feature: "Age e executa ações com sua aprovação", insights: true, bi: false, llm: false, analytics_ai: false },
  { feature: "Especialistas por disciplina (mídia, produto, CRM...)", insights: true, bi: false, llm: false, analytics_ai: false },
  { feature: "Marketplace: Mercado Livre, ads + vendas", insights: true, bi: false, llm: false, analytics_ai: false },
  { feature: "Auto-Tagging e auditoria de tracking", insights: true, bi: false, llm: false, analytics_ai: false },
  { feature: "Sem SQL, sem BI developer, sem setup", insights: true, bi: false, llm: "parcial", analytics_ai: "parcial" },
  { feature: "Histórico e memória da sua operação", insights: true, bi: false, llm: false, analytics_ai: "limitado" },
];

const FAQ_ITEMS = [
  // Produto e operação
  {
    q: "O que é, em uma frase, o Seraik Insights?",
    a: "É uma plataforma com Painel Executivo, Mesas de Performance, Chat e Squad de Analistas conectados às suas fontes reais. Entrega diagnóstico, análise e ação em tempo real, sem dashboard pra montar nem SQL pra escrever.",
  },
  {
    q: "Para quem o produto é feito?",
    a: "Para C-level, heads de growth/performance, agências multi-cliente e sellers de marketplace que precisam decidir todo dia sobre receita, mídia, conversão, retenção e operação, e não querem depender de relatório semanal pra fazer isso.",
  },
  {
    q: "Como o Painel Executivo funciona?",
    a: "Cada workspace escolhe um segmento (ecommerce, lead_gen, real_estate, SaaS, publisher, auto, insurance, healthcare e variantes Adobe) e o Painel monta KPIs primários, meta vs realizado, probabilidade Bayesiana de bater meta, tendência, mídia consolidada, unit economics, funil customizável por segmento, MMM e Plano de Ações, tudo com fonte priorizada e auto-fallback quando uma falha. O catálogo tem 89 cards com diagnóstico ativo (BlockAnalyzer) em cada bloco, e você personaliza por família (Overview, Sales, Media, Models, Behavior) ativando, escondendo e reordenando o que cada workspace mostra.",
  },
  {
    q: "Dá pra comparar produtos, fases, horários e meses?",
    a: "Sim. Os Cards de Comparação cruzam Produtos (empreendimentos, linhas, SKUs), Fases do ciclo (lançamento, estoque, promo) e Hora do dia (hoje vs ontem vs semana passada) extraindo a dimensão direto do nome da campanha ou do dicionário do workspace. O Funil Comparativo Multi-Mês compara até 4 meses lado a lado, com YoY (ano vs ano), e o módulo Behavior Temporal mostra padrões por dia da semana, hora do dia, dia do mês, mês e heatmap horário (weekend vs weekday).",
  },
  {
    q: "O Seraik faz Marketing Mix Modeling (MMM)?",
    a: "Faz, com profundidade de boutique. A família Models tem 17 cards: contribuição por canal além do last-click, curva de saturação (Hill function), otimizador de budget, cenários what-if (-50% / +50% / 2x e custom), recorte geo, recorte por produto, comparativo MMM vs Last-Click, comparativo MMM vs Forecast Realizado, e transparência de confiança do modelo (R², janela de dados, holdout out-of-sample). Você sabe quando confiar e quando esperar mais dado.",
  },
  {
    q: "O que é uma Mesa de Performance?",
    a: "É um workspace de investigação dedicado a um cliente ou contexto, com várias fontes conectadas e contexto permanente. Trabalha com janela de até 13 meses para análises com sazonalidade e YoY, é multi-fonte (GA4, Adobe, Ads, BigQuery, planilhas, CRM) e o Squad continua a conversa anterior, não começa do zero a cada pergunta.",
  },
  {
    q: "Como funciona o Chat?",
    a: "Você pergunta em português. A aunica (Squad Lead) classifica a pergunta e a fonte ativa e aciona o especialista certo entre os 12 analistas do Squad (Mayra, Sofia, José, Lucas, Elena, Rafael, Beatriz, Camila, Fernanda, André, Maria, Pedro ou Igor), que responde com contexto de negócio, números e ação recomendada.",
  },
  {
    q: "Quem são os analistas do Squad Seraik?",
    a: "Squad ativo com 12 especialistas + aunica como Squad Lead: Mayra (Negócios), Sofia (Dados & Tendências), José (Performance Digital), Lucas (MarTech), Elena (Adobe), Rafael (Programática), Beatriz (CRO), Camila (SEO), Fernanda (CRM), André (Analytics Engineering), Maria (Cientista de Dados), Pedro (Customer Success), Igor (Atribuição). A aunica (Squad Lead) classifica a pergunta e aciona o analista certo conforme contexto, fonte e disciplina. Diego (E-commerce) e Bruno (Inteligência de Mercado) chegando.",
  },

  // Conexões e dados
  {
    q: "Quais fontes de dados eu posso conectar?",
    a: "GA4, Adobe Analytics, Google Ads, Meta Ads, LinkedIn Ads, TikTok Ads, Search Console, GTM (server-side), BigQuery, Google Sheets, SharePoint Excel, HubSpot, Amplitude, Mercado Livre, Magalu, MoEngage, Insider, SFTP e Segment CDP (server-side) estão disponíveis hoje. RD Station, VTEX, Shopify e Amazon BR em breve. Total: 18 conectores ativos.",
  },
  {
    q: "Como conecto uma fonte?",
    a: "A conexão é feita por um wizard guiado de 5 passos: autoriza o acesso pela própria ferramenta (Google, Meta, Mercado Livre, Salesforce) ou cola a credencial fornecida pela plataforma de origem (MoEngage, Insider) ou plugamos no envio programado dos seus arquivos. Sem precisar de time de TI no meio.",
  },
  {
    q: "Os dados ficam armazenados no Seraik Insights?",
    a: "Não. Seus dados são consultados em tempo real direto na fonte, com cache curto só para deixar a resposta instantânea. As credenciais ficam protegidas e criptografadas. Nada de cópia integral do seu data warehouse.",
  },
  {
    q: "E se uma fonte cair ou ficar indisponível?",
    a: "A plataforma aplica fallback automático na cadeia de fontes priorizadas (ex: Planilha → ERP → Analytics). E quando uma autorização precisa renovar, isso acontece de forma silenciosa. Você não fica reconectando a cada sessão.",
  },
  {
    q: "Posso conectar planilhas ou arquivos?",
    a: "Sim. Google Sheets via OAuth, CSV/XLSX via upload direto e SFTP para integração programada com sistemas internos. Tudo entra no metric resolver com a mesma prioridade das outras fontes.",
  },

  // Multi-tenant e workspaces
  {
    q: "Posso ter vários clientes no mesmo ambiente?",
    a: "Sim. O modelo é multi-workspace por organização: cada cliente é um workspace isolado, com membros próprios, fontes próprias e contexto próprio. Agências usam isso pra gerenciar 10+ clientes em uma só conta.",
  },
  {
    q: "Como criar um workspace novo?",
    a: "Tem um wizard de onboarding que pergunta segmento (ecommerce, lead_gen, real_estate, SaaS, etc.), conecta as primeiras fontes e já sugere os blocos do Painel adequados ao seu negócio. Em minutos sai com o Painel populado e o Squad pronto pra responder.",
  },
  {
    q: "Consigo importar dados de planilha e CSV no início?",
    a: "Sim. O Excel Mapper aceita XLSX/CSV em bulk, detecta o schema automaticamente, mapeia colunas pros conceitos do produto (revenue, spend, channel, etc.) e aplica concept rules. Ideal pra começar sem depender de OAuth de fonte ainda.",
  },
  {
    q: "Como funciona o convite de membros?",
    a: "Você convida por e-mail. O membro recebe link de aceite, cria senha e entra direto no workspace. Roles: owner, admin, member, viewer, cada um com permissões diferentes em conexões, metas e configuração.",
  },
  {
    q: "Um membro vê os dados de outros workspaces?",
    a: "Não. Workspaces são isolados via Row-Level Security do Supabase. Um viewer convidado pra um workspace só vê dados desse workspace, mesmo dentro da mesma organização.",
  },

  // Beta 4 — features novas (10/06/2026)
  {
    q: "O que muda no Beta 4 do produto?",
    a: "Beta 4 trouxe Service Account Universal (sem depender de login pessoal pra GA4/Search Console/BigQuery), Plano de Crescimento operacional (4 sprints completas: Reverso, Saturação, Weekly, Omnichannel), motor de análise que aciona as fontes de dados no Mesa de Investigação, Satélite GPS de Marketing, ConfirmAction 2-step pra ações sensíveis e Credenciais Organizacionais por domínio. Tudo em produção.",
  },
  {
    q: "Como funciona o Satélite GPS de Marketing?",
    a: "É o módulo de orientação contínua: roda diariamente, lê os sinais do workspace (anomalias, gasto sem conversão, ROAS baixo, MMM com saturação) e devolve uma fila de rotas priorizadas com horizonte temporal (AGORA / HOJE / ESTA SEMANA / ESTE MÊS). Tom imperativo, badge AÇÃO DA SEMANA na top-1, custo de não agir projetado pra fim do mês e confiança amarrada com R²/MAPE do MMM quando treinado. Cada rota você marca Feito / Lembrar / Pular, com follow-up 24h pós-decisão.",
  },
  {
    q: "Por que vocês usam Service Account em vez de OAuth pra Google e Adobe?",
    a: "Confiabilidade enterprise. OAuth depende do login pessoal de um usuário; Service Account é credencial server-side compartilhada entre workspaces do mesmo domínio. Resultado: não expira sozinho, não some quando o admin troca de função, não precisa reconectar a cada janela do navegador. Cliente Adobe + Google Analytics + BigQuery todos rodam server-side sem JS no navegador, contornando ad blockers e tracking prevention.",
  },
  {
    q: "O que é o Plano de Crescimento?",
    a: "É o módulo de planejamento autônomo que conversa antes de propor. Você diz a meta, ele responde com Reverso meta→budget, três cenários (-30% / planejado / +30%), curva de saturação dos canais (Hill), plano semanal 4 semanas, comparativo de eficiência por canal, Omnichannel CRM/SEO/Push e Resumo Executivo. Pensado como diretor de mídia faria: pergunta período, fontes disponíveis, objetivo, antes de calcular.",
  },

  // Tarefas, alertas e autonomia
  {
    q: "Como funciona o sistema de Tarefas e Alertas?",
    a: "O Squad detecta anomalias e oportunidades em background, gera tarefas com severidade, ação recomendada e impacto estimado. Aparecem na sidebar do Painel Executivo. Você aprova, atribui ou recusa, com motivo.",
  },
  {
    q: "O módulo Autônomo já está disponível?",
    a: "Sim, em Beta 4. Plano de Crescimento operacional + ConfirmAction 2-step. Nenhuma ação executa sozinha — o Autônomo SUGERE plano de mídia, distribuição de budget, ajustes em campanhas, mas precisa do seu OK via código de confirmação (estilo HubSpot). Cada ação fica registrada no workspace_action_log imutável. Pra ações reversíveis, basta 1 clique; pra sensíveis (orçamento, pause), 2-step.",
  },
  {
    q: "O Squad executa ação na minha conta sem aprovar?",
    a: "Nunca. Toda ação tem confirmation gate explícito. Mesmo no Autônomo, o modelo é detectar → sugerir → esperar aprovação. Nenhum clique automático em campanha, nenhuma alteração silenciosa. Log imutável em workspace_action_log de tudo que foi sugerido, aprovado ou recusado.",
  },

  // Retail
  {
    q: "Quando o módulo Retail fica disponível?",
    a: "Em breve. A integração com Mercado Livre (OAuth + vendas + Product Ads + ROAS por SKU + alertas de ruptura) está em validação final. Shopee, Amazon e VTEX vêm em sequência. Se você é seller e quer entrar no piloto, fala com o time comercial.",
  },

  // Segurança e compliance
  {
    q: "Quais garantias de segurança vocês têm?",
    a: "Credenciais protegidas em vault, isolamento por workspace, HTTPS, auditoria de cada ação. Seus dados ficam restritos ao seu ambiente. Não são compartilhados entre clientes nem usados para treinar nada.",
  },
  {
    q: "Vocês atendem empresas reguladas (BACEN, LGPD, SUSEP)?",
    a: "Sim, no modelo On-Premise. Empacotamos a plataforma para deploy no seu ambiente. Dados nunca saem da sua infraestrutura. Compatível com LGPD, BACEN, SUSEP e ANS.",
  },

  // Comparativos
  {
    q: "Qual a diferença para Tableau, Power BI ou Looker?",
    a: "Eles mostram dados. Você ainda precisa de analista para interpretar e propor ação. Seraik Insights responde por quê aconteceu, o que fazer e antecipa o que pode acontecer. É a passagem do dashboard para a decisão operacional.",
  },
];

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const half = Math.ceil(FAQ_ITEMS.length / 2);
  const col1 = FAQ_ITEMS.slice(0, half);
  const col2 = FAQ_ITEMS.slice(half);

  const FAQItem = ({ item, index }: { item: typeof FAQ_ITEMS[0]; index: number }) => (
    <div className="rounded-xl border border-border/30 bg-background overflow-hidden">
      <button
        className="w-full flex items-center justify-between p-4 text-left gap-4 hover:bg-muted/10 transition-colors"
        onClick={() => setOpenIndex(openIndex === index ? null : index)}
      >
        <span className="text-sm font-medium">{item.q}</span>
        <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${openIndex === index ? "rotate-90" : ""}`} />
      </button>
      {openIndex === index && (
        <div className="px-4 pb-4">
          <p className="text-sm text-muted-foreground leading-relaxed">{item.a}</p>
        </div>
      )}
    </div>
  );

  return (
    <section id="faq" className="border-t border-border/40 py-24 bg-muted/10">
      <div className="container max-w-5xl">
        <div className="text-center mb-12">
          <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Dúvidas frequentes</p>
          <h2 className="text-3xl font-bold">Antes de começar</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="space-y-3">
            {col1.map((item, i) => <FAQItem key={item.q} item={item} index={i} />)}
          </div>
          <div className="space-y-3">
            {col2.map((item, i) => <FAQItem key={item.q} item={item} index={i + half} />)}
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [formStarted, setFormStarted] = useState(false);
  const onFieldInteract = (field: string) => {
    if (!formStarted) {
      trackFormStart();
      setFormStarted(true);
    }
    trackFormFieldInteract({ field_name: field });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const res = await fetch("/api/enterprise-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, planInterest: "contact_landing" }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || "Erro ao enviar");
      }
      setSubmitted(true);
      // Track conversao — LEAD
      trackFormSubmit({
        has_company: !!form.company,
        has_message: !!form.message,
        source: "landing_contato",
      });
    } catch (err: any) {
      setError(err.message || "Erro ao enviar. Tente novamente.");
      trackFormError({ error: err.message || "submit_error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contato" className="border-t border-border/40 bg-muted/5 py-20">
      <div className="container max-w-lg">
        <div className="text-center mb-10">
          <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Vamos conversar</p>
          <h2 className="text-2xl font-bold">Fale com a aunica</h2>
          <p className="text-muted-foreground text-sm mt-2">Retorno em até 1 dia útil. Sem pressão de venda.</p>
        </div>

        {submitted ? (
          <div className="text-center py-10">
            <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
              <Check className="h-6 w-6 text-green-500" />
            </div>
            <h3 className="font-semibold mb-2">Mensagem recebida!</h3>
            <p className="text-sm text-muted-foreground">Um especialista da aunica vai entrar em contato em até 1 dia útil.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" data-form="fale_com_aunica">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">Nome *</label>
                <input required
                  className="w-full rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                  placeholder="Seu nome"
                  value={form.name}
                  onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                  onFocus={() => onFieldInteract('name')}
                />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1.5 block">E-mail *</label>
                <input required type="email"
                  className="w-full rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                  placeholder="você@empresa.com"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  onFocus={() => onFieldInteract('email')}
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Empresa</label>
              <input
                className="w-full rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50"
                placeholder="Nome da empresa"
                value={form.company}
                onChange={(e) => setForm(f => ({ ...f, company: e.target.value }))}
                onFocus={() => onFieldInteract('company')}
              />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1.5 block">Como podemos ajudar?</label>
              <textarea rows={4}
                className="w-full rounded-md border border-border/50 bg-muted/20 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/50 resize-none"
                placeholder="Fale sobre seu negócio, suas fontes de dados, o que você quer resolver..."
                value={form.message}
                onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                onFocus={() => onFieldInteract('message')}
              />
            </div>
            {error && <p className="text-xs text-red-400">{error}</p>}
            <button type="submit" disabled={submitting}
              className="w-full rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white py-2.5 text-sm font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-violet-900/30 border-0"
            >
              {submitting
                ? <span className="h-4 w-4 rounded-full border-2 border-background/30 border-t-background animate-spin" />
                : <><ArrowRight className="h-4 w-4" /> Enviar mensagem</>
              }
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

export function Hero() {
  return (
    <div className="min-h-screen bg-background">

      {/* ── Header ────────────────────────────────────────────────────────────── */}
      <header className="fixed top-0 w-full border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="container flex h-16 items-center justify-between">
          <Link href="/" className="flex items-center gap-3">
            {/* Beta 04.03.005 (10/06/2026): logo COLORIDO sempre.
                Padrão unificado, sem variante white. */}
            <img
              src="/logos/seraik-insights/seraik-insights-color.svg"
              alt="Seraik Insights"
              className="h-10 w-auto block select-none"
              draggable={false}
            />
          </Link>
          {/* Beta 04.06.003 (10/06/2026, Ricardo P0): menu reorganizado pra contar
              uma jornada — Por que (a dor) → O que é (solução) → Como opera
              (ciclo + módulos) → Squad (quem analisa) → Cases (provas) → Começar.
              Antes era: O que é · Módulos · Data Lakes · Squad · Cases · Como
              começar · FAQ. Data Lakes virou sub de Como opera. FAQ saiu do
              header (continua existindo na página, link a partir do rodapé). */}
          <nav className="flex items-center gap-1.5 lg:gap-2">
            <Link href="#por-que" className="text-sm text-muted-foreground hover:text-violet-300 transition-colors hidden md:block px-2 py-1">
              Por que
            </Link>
            <span className="text-muted-foreground/30 hidden md:inline">·</span>
            <Link href="#o-que-e" className="text-sm text-muted-foreground hover:text-violet-300 transition-colors hidden md:block px-2 py-1">
              A solução
            </Link>
            <span className="text-muted-foreground/30 hidden md:inline">·</span>
            <Link href="#como-funciona" className="text-sm text-muted-foreground hover:text-violet-300 transition-colors hidden md:block px-2 py-1">
              Como opera
            </Link>
            <span className="text-muted-foreground/30 hidden md:inline">·</span>
            <Link href="#squad" className="text-sm text-muted-foreground hover:text-violet-300 transition-colors hidden md:block px-2 py-1">
              Squad
            </Link>
            <span className="text-muted-foreground/30 hidden md:inline">·</span>
            <Link href="#cases" className="text-sm text-muted-foreground hover:text-violet-300 transition-colors hidden md:block px-2 py-1">
              Cases
            </Link>
            <span className="text-muted-foreground/30 hidden md:inline">·</span>
            <Link href="#planos" className="text-sm text-muted-foreground hover:text-violet-300 transition-colors hidden md:block px-2 py-1">
              Começar
            </Link>
            <ThemeToggle className="ml-1" />
            <div className="w-px h-5 bg-border/40 mx-2 hidden md:block" />
            <Button asChild variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground">
              <Link href="/login">Entrar</Link>
            </Button>
            <Button asChild size="sm" className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 border-0 shadow-md shadow-violet-900/30">
              <a href="#contato"
                 onClick={() => trackCtaClick({ cta_label: 'Falar com especialista', cta_location: 'header', cta_destination: '#contato' })}>
                Falar com especialista
              </a>
            </Button>
          </nav>
        </div>
      </header>
      {/* Beta 04.06.x: tracking GA4/GTM scroll + section views */}
      <LandingTracker />

      {/* ── Hero ── Beta 04.06.001 restyle: paleta violeta + animação + mockup preview ── */}
      <section className="relative overflow-hidden pt-16">
        {/* Background decorativo: 2 blobs violeta animados + grid sutil + glow center */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[10%] left-[15%] w-[600px] h-[600px] bg-violet-500/15 blur-[140px] rounded-full seraik-blob-a" />
          <div className="absolute top-[40%] right-[10%] w-[500px] h-[500px] bg-indigo-500/12 blur-[120px] rounded-full seraik-blob-b" />
          <div className="absolute top-[60%] left-[50%] -translate-x-1/2 w-[800px] h-[300px] bg-fuchsia-500/8 blur-[160px] rounded-full" />
          <div
            className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        <div className="container relative py-24 lg:py-28">
          {/* Layout assimétrico: coluna texto + mockup preview lado-a-lado em desktop */}
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
            {/* Coluna esquerda — copy */}
            <div className="flex flex-col items-start text-left">
              {/* Beta 04.06.006: light mode invisivel. Reforcado contraste:
                  text-violet-700 dark:text-violet-200, bg mais opaco. */}
              <div className="seraik-fade-up inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full border border-violet-500/40 bg-violet-500/15 dark:bg-violet-500/10 mb-6 backdrop-blur-sm seraik-glow-violet">
                <Sparkles className="h-3.5 w-3.5 text-violet-600 dark:text-violet-300" />
                <span className="text-violet-700 dark:text-violet-200 text-xs font-medium tracking-wide">A squad de elite que grandes empresas têm, agora ao seu alcance</span>
              </div>

              <h1 className="seraik-fade-up-d1 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-[5.2rem] lg:leading-[1.05]">
                Pare de analisar.
                <span className="block mt-2 seraik-headline-gradient">Comece a decidir.</span>
              </h1>

              <p className="seraik-fade-up-d2 text-lg text-muted-foreground max-w-xl mt-6 leading-relaxed">
                <span className="text-foreground font-semibold">Seraik Insights</span> transforma dados em decisão:
                em tempo real, em português, com ação recomendada. Painel Executivo, Mesas de Performance e
                conversa direta com seu Squad de Analistas.
              </p>
              <p className="seraik-fade-up-d3 mt-4 text-base text-foreground/80 font-medium">
                Sem dashboard pra montar. Sem relatório pra esperar. Sem SQL pra escrever.
              </p>

              <div className="seraik-fade-up-d4 flex flex-col sm:flex-row gap-3 mt-10 items-stretch sm:items-center">
                <Button asChild size="lg" className="text-base gap-2 px-7 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 border-0 shadow-lg shadow-violet-900/40">
                  <Link href="#contato"
                    onClick={() => trackCtaClick({ cta_label: 'Falar com especialista', cta_location: 'hero', cta_destination: '#contato' })}>
                    Falar com especialista <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="text-base border-violet-500/30 hover:bg-violet-500/10 hover:border-violet-400/60">
                  <Link href="#como-funciona"
                    onClick={() => trackCtaClick({ cta_label: 'Ver como funciona', cta_location: 'hero', cta_destination: '#como-funciona' })}>
                    Ver como funciona
                  </Link>
                </Button>
              </div>

              <a
                href={ONE_PAGER_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackOnepagerDownload({ source: 'hero' })}
                className="seraik-fade-up-d5 mt-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-violet-300 transition-colors"
              >
                <Download className="h-3.5 w-3.5" /> ou baixe o one-pager executivo (PDF · 2 páginas)
              </a>
            </div>

            {/* Coluna direita — mockup preview do Painel (BLOCO 5: tilt 3D via data-seraik-tilt) */}
            <div data-seraik-tilt className="seraik-fade-up-d3 hidden lg:block relative">
              <div className="seraik-float relative rounded-2xl border border-violet-500/25 bg-gradient-to-br from-violet-500/10 via-card to-indigo-500/10 backdrop-blur-xl p-5 shadow-2xl shadow-violet-900/20">
                {/* Mock header */}
                <div className="flex items-center justify-between pb-3 border-b border-violet-500/15">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
                    <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                    <span className="ml-2 text-[11px] text-muted-foreground">Painel · últimos 7 dias</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-wide text-violet-300/60">live</span>
                </div>

                {/* KPIs row */}
                <div className="grid grid-cols-3 gap-3 mt-4">
                  {[
                    { label: 'Vendas', value: 'R$ 287k', delta: '+12,4%', up: true },
                    { label: 'ROAS', value: '4,8x',     delta: '+0,3', up: true },
                    { label: 'CPA',   value: 'R$ 38',   delta: '-8,1%', up: false },
                  ].map((k) => (
                    <div key={k.label} className="rounded-lg border border-violet-500/15 bg-violet-500/5 p-3">
                      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{k.label}</p>
                      <p className="text-lg font-bold mt-1 text-foreground">{k.value}</p>
                      <p className={`text-[10px] mt-0.5 ${k.up ? 'text-emerald-400' : 'text-rose-400'}`}>
                        {k.delta} vs período anterior
                      </p>
                    </div>
                  ))}
                </div>

                {/* Mock chart */}
                <div className="mt-4 rounded-lg border border-violet-500/15 bg-violet-500/5 p-4">
                  <div className="flex justify-between items-baseline mb-3">
                    <span className="text-xs text-foreground font-medium">Vendas × Mídia</span>
                    <span className="text-[10px] text-violet-300/60">Selo · GA4 + datalake</span>
                  </div>
                  <svg viewBox="0 0 300 80" className="w-full h-20">
                    <defs>
                      <linearGradient id="seraik-area" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#a855f7" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M0,60 L40,55 L80,50 L120,40 L160,42 L200,28 L240,30 L280,18 L300,12 L300,80 L0,80 Z" fill="url(#seraik-area)" />
                    <path d="M0,60 L40,55 L80,50 L120,40 L160,42 L200,28 L240,30 L280,18 L300,12" fill="none" stroke="#a855f7" strokeWidth="2" />
                    <path d="M0,68 L40,65 L80,62 L120,58 L160,55 L200,50 L240,48 L280,45 L300,42" fill="none" stroke="#6366f1" strokeWidth="1.5" strokeDasharray="3 3" opacity="0.7" />
                  </svg>
                </div>

                {/* Insight pill */}
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-violet-500/25 bg-violet-500/8 p-3">
                  <div className="w-7 h-7 rounded-full bg-violet-500/20 border border-violet-500/30 flex items-center justify-center shrink-0">
                    <BrainCircuit className="w-3.5 h-3.5 text-violet-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-foreground font-medium">Sofia · análise de tendência</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                      Meta Ads acelerou 22% nos últimos 3 dias. Recomendo investigar criativos novos antes de escalar.
                    </p>
                  </div>
                </div>
              </div>
              {/* Glow atrás do mockup */}
              <div className="absolute -inset-8 -z-10 bg-violet-500/10 blur-3xl rounded-full" />
            </div>
          </div>

          {/* Fontes agrupadas */}
          <div className="seraik-fade-up-d6 mt-20 lg:mt-24">
            <p className="text-[11px] text-muted-foreground mb-5 uppercase tracking-widest text-center">
              Conecta com ferramentas de mídia, analytics, dados, e-commerce e CRM
            </p>
            <div className="space-y-3 max-w-5xl mx-auto">
              {SOURCE_GROUPS.map((group) => (
                <div key={group.category} className="flex flex-wrap items-center justify-center gap-2">
                  <span className="text-[10px] uppercase tracking-wider text-violet-300/70 font-semibold pr-3 min-w-[110px] text-right">
                    {group.category}
                  </span>
                  {group.sources.map((s) => (
                    <span
                      key={`${group.category}-${s.name}`}
                      className={`seraik-chip-lift inline-flex items-center px-3 py-1.5 rounded-full border text-xs ${
                        s.available
                          ? "border-border/50 bg-card/50 text-foreground/80 backdrop-blur-sm"
                          : "border-border/30 bg-transparent text-muted-foreground/40"
                      }`}
                    >
                      {s.name}
                      {!s.available && <span className="ml-1.5 text-[9px] opacity-60">em breve</span>}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── O que é / Para quem é ─────────────────────────────────────────── */}
      <LandingV2Blocks />

      <section id="o-que-e" className="border-t border-border/40 py-20 relative overflow-hidden">
        {/* Decorativo: blur radial sutil */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-blue-500/5 blur-3xl rounded-full pointer-events-none" />

        <div className="container max-w-5xl relative">
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-12 items-start">

            {/* O que é */}
            <div className="rounded-2xl border border-border/40 bg-card/40 p-8 space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-border/30">
                <div className="h-12 w-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-[10px] text-violet-300 uppercase tracking-widest font-semibold">O que é</p>
                  <p className="text-sm font-semibold">Plataforma de inteligência de decisão</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold leading-snug">
                A inteligência analítica que grandes empresas <span className="text-blue-400">sonham ter.</span>
              </h2>

              <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
                <p>
                  <span className="text-foreground font-semibold">Seraik Insights</span> não é mais uma ferramenta.
                  É uma <span className="text-foreground font-medium">plataforma de decisão operacional</span> que
                  consolida seu ecossistema de dados, ativa um Squad de analistas digitais 24/7 e
                  <span className="text-foreground font-medium"> entrega resposta com ação</span>, em segundos,
                  no idioma do seu negócio.
                </p>
                <p>
                  Onde antes seu time levava dias cruzando planilhas e brigando com BI, agora a operação
                  acontece <span className="text-foreground font-medium">em tempo real</span>, com Painel Executivo
                  pronto, Mesas de Performance multi-fonte, Chat com especialistas e integração nativa com seu Data Lake.
                </p>
              </div>

              {/* Eyebrow numérico de impacto */}
              <div className="grid grid-cols-3 gap-3 pt-4 border-t border-border/30">
                <div>
                  <p className="text-2xl font-bold text-blue-400 leading-none">10</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">módulos</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-400 leading-none">20+</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">conectores</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-400 leading-none">24/7</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-1">Squad ativo</p>
                </div>
              </div>
            </div>

            {/* Para quem é */}
            <div className="rounded-2xl border border-border/40 bg-card/40 p-8 space-y-5">
              <div className="flex items-center gap-3 pb-4 border-b border-border/30">
                <div className="h-12 w-12 rounded-xl bg-violet-500/10 border border-violet-500/30 flex items-center justify-center">
                  <Target className="h-6 w-6 text-violet-400" />
                </div>
                <div>
                  <p className="text-[10px] text-violet-400 uppercase tracking-widest font-semibold">Para quem é</p>
                  <p className="text-sm font-semibold">Times que decidem todo dia</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold leading-snug">
                Para quem opera com <span className="text-violet-400">decisão diária.</span>
              </h2>

              <ul className="space-y-3.5">
                {[
                  { icon: Briefcase, who: "Diretoria e C-level", desc: "CMO, CRO, CFO que querem foto executiva ao acordar, não relatório de sexta." },
                  { icon: LineChart, who: "Heads de Growth e Performance", desc: "Times que cruzam mídia, SEO, CRO e CRM, e querem agir antes da queda virar crise." },
                  { icon: Layers, who: "Agências e operações multi-cliente", desc: "10, 20, 50 clientes em workspaces isolados, sem perder contexto nem velocidade." },
                  { icon: Activity, who: "Sellers e marketplace", desc: "Mercado Livre, ROAS por SKU, ruptura, Product Ads, num dashboard que conversa de volta." },
                  { icon: Database, who: "Empresas com Data Lake", desc: "BigQuery, Snowflake, Redshift, prontos para receber perguntas em linguagem natural." },
                ].map(({ icon: Icon, who, desc }) => (
                  <li key={who} className="flex items-start gap-3">
                    <div className="h-8 w-8 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon className="h-4 w-4 text-violet-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold">{who}</p>
                      <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ── Dores que resolvemos ───────────────────────────────────────────── */}
      <section id="por-que" className="border-t border-border/40 py-20">
        <div className="container max-w-5xl">
          <div className="text-center mb-12">
            <p className="text-xs text-red-400 uppercase tracking-widest mb-2 font-semibold">A conta que ninguém faz</p>
            <h2 className="text-3xl font-bold leading-tight">Quanto sua empresa está perdendo <span className="text-red-400">enquanto espera relatório?</span></h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
              Três números que toda operação vive, e poucos param para calcular.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="p-6 rounded-xl border border-red-500/20 bg-red-500/5 text-center space-y-3">
              <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
                <Clock className="h-6 w-6 text-red-400" />
              </div>
              <p className="text-4xl font-bold text-red-400">67%</p>
              <p className="text-sm font-medium">do tempo do analista</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                gasto extraindo e formatando dados, não analisando. Uma empresa que investe R$15k/mês em um analista de dados está, na prática, pagando R$10k por Excel e R$5k por decisão real.
              </p>
            </div>

            {/* Beta 04.06.002: paleta alerta unificada em rose com 3 intensidades */}
            <div className="p-6 rounded-xl border border-rose-500/20 bg-rose-500/5 text-center space-y-3">
              <div className="h-12 w-12 rounded-full bg-rose-500/10 flex items-center justify-center mx-auto">
                <TrendingDown className="h-6 w-6 text-rose-300" />
              </div>
              <p className="text-4xl font-bold text-rose-300">R$ 40k</p>
              <p className="text-sm font-medium">perdidos por mês em mídia</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                em desperdício para quem investe R$200k/mês em mídia. São 20% do budget rodando campanhas com ROAS abaixo de 2x por semanas, porque o relatório sai toda sexta.
              </p>
            </div>

            <div className="p-6 rounded-xl border border-rose-500/15 bg-rose-500/3 text-center space-y-3">
              <div className="h-12 w-12 rounded-full bg-rose-500/8 flex items-center justify-center mx-auto">
                <AlertTriangle className="h-6 w-6 text-rose-200" />
              </div>
              <p className="text-4xl font-bold text-rose-200">48h</p>
              <p className="text-sm font-medium">de lag médio para agir</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                entre uma anomalia acontecer e o time perceber. Em e-commerce, isso equivale
                a perder dois dias de receita no pico de conversão.
              </p>
            </div>
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Seraik Insights não é mais uma ferramenta de BI. É a passagem{" "}
            <span className="text-foreground font-medium">da análise reativa à decisão operacional contínua.</span>
          </p>
        </div>
      </section>

      {/* ── Módulos da plataforma ───────────────────────────────────────────── */}
      <section id="como-funciona" className="border-t border-border/40 py-24 bg-muted/10">
        <div className="container max-w-6xl">
          <div className="text-center mb-16">
            <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Os módulos</p>
            <h2 className="text-3xl font-bold">Uma plataforma. Vários módulos integrados.</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
              Painel Executivo no centro. Mesas, Chat, Conexões e Auditoria orbitando.
              Tudo conversa entre si: o contexto de uma análise vira input de outra.
            </p>
          </div>

          {/* #355/#324 (29/05/2026): diagrama de arquitetura ilustrando os módulos */}
          <div className="mb-10">
            <ArchitectureDiagram />
          </div>

          {/* Painel Executivo — destaque */}
          <div className="mb-8 p-7 rounded-2xl border-2 border-blue-500/50 bg-blue-500/5 shadow-lg shadow-blue-500/10">
            <div className="grid md:grid-cols-3 gap-6 items-center">
              <div className="md:col-span-2 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <Gauge className="h-5 w-5 text-blue-400" />
                  </div>
                  <h3 className="text-2xl font-bold">Painel Executivo</h3>
                  <span className="text-[10px] bg-blue-500 text-white px-2 py-0.5 rounded-full font-semibold">DESTAQUE</span>
                  <span className="text-[10px] bg-green-500/20 text-green-400 border border-green-500/30 px-2 py-0.5 rounded-full font-semibold">DISPONÍVEL</span>
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Foto executiva em tempo real. Catálogo de 89 cards com diagnóstico ativo (BlockAnalyzer)
                  em cada bloco. KPIs por segmento, meta vs realizado, probabilidade Bayesiana de bater meta,
                  tendência, gap analysis, mídia consolidada (planejado vs realizado), unit economics, MMM,
                  forecast, funil comparativo multi-mês (até 4 meses + YoY), comportamento temporal,
                  Plano de Ações executivo e alertas. Cada bloco com sua fonte e prioridade. Quando uma
                  falha, o sistema busca a próxima. Personalizável por família (Overview, Sales, Media,
                  Models, Behavior) e por subgrupo, com analista executivo embutido no chat lateral.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  {[
                    "Foto Executiva", "Plano de Ações", "Probabilidade de Meta (Bayesiana)",
                    "Tendência", "Meta × Realizado", "Cockpit Pro",
                    "Mídia + Planejado", "Receita/Pedidos por Hora", "Unit Economics",
                    "Funil Comparativo Multi-Mês (YoY)", "Funil Customizável por Segmento",
                    "Forecast 35d", "MMM Saturation + Optimizer + What-If + Geo",
                    "Behavior Temporal (DoW, HoD, heatmap)",
                    "Compare Produto/Fase", "Tasks & Alertas",
                  ].map((b) => (
                    <span key={b} className="text-[11px] px-2 py-1 rounded-md bg-background/60 border border-border/50 text-muted-foreground">{b}</span>
                  ))}
                </div>
              </div>
              <div className="text-xs text-muted-foreground space-y-2 border-l border-border/30 md:pl-6">
                <p className="font-semibold text-foreground">Perguntas que ele responde:</p>
                <p>▸ Qual é o status hoje?</p>
                <p>▸ Para onde estamos indo?</p>
                <p>▸ Como estamos vs meta?</p>
                <p>▸ Qual a projeção do mês?</p>
                <p>▸ Onde está o risco e a oportunidade?</p>
              </div>
            </div>
          </div>

          {/* Beta 03.04.099 — separado em 2 grids: Disponível (6 cards perfeitos
              em 2 rows de 3) + Em desenvolvimento (3 cards em 1 row de 3).
              Reorder visual + grouping pra evitar mix soon/disponível na mesma row. */}

          {/* Disponíveis agora — 2 rows × 3 cards */}
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                name: "Chat com o Squad",
                icon: MessageSquare,
                desc: "Pergunte em português. Mayra, Sofia, José, Lucas, Elena ou aunica é acionado automaticamente conforme o tipo de pergunta e a fonte ativa.",
                bullets: ["Roteamento automático por especialidade e fonte", "Memória de contexto por conversa", "GA4, Adobe, Ads, Search Console, BigQuery, ML"],
              },
              {
                name: "Mesa de Investigação",
                icon: Layers,
                desc: "Workspace multi-fonte com contexto permanente e janela de até 13 meses. GA4, Adobe, Ads, BigQuery, CRM e planilhas na mesma mesa. O Squad continua a conversa anterior, sem reset.",
                bullets: ["Janela de 13 meses (sazonalidade + YoY)", "Multi-fonte na mesma análise", "Detecção de anomalias + alertas"],
              },
              {
                name: "Conexões multi-fonte",
                icon: Database,
                desc: "OAuth para 16+ fontes: GA4, Google/Meta/TikTok/LinkedIn Ads, Adobe Analytics, GSC, GTM, BigQuery, Sheets, OneDrive/Sharepoint Excel, Salesforce, MoEngage, Insider, SFTP, Mercado Livre.",
                bullets: ["OAuth oficial + isolamento por workspace", "Sync 1h pra planilhas vivas (Google Sheets / OneDrive / Sharepoint)", "Renovação automática de credenciais"],
              },
              {
                name: "MMM · Marketing Mix Modeling",
                icon: TrendingDown,
                desc: "Família Models com 17 cards. Regressão agregada pós-cookies: contribuição por canal, curva de saturação (Hill), optimizer de budget, cenários what-if (-50% / +50% / 2x), recortes por geo e produto, comparativo MMM vs Last-Click e MMM vs Forecast Realizado. R² + holdout out-of-sample pra honestidade do modelo.",
                bullets: ["Saturation curve por canal (Hill function)", "Optimizer + What-If + Geo + Produto", "MMM vs Last-Click + vs Forecast Realizado"],
              },
              {
                name: "Excel Mapper + Planilhas Vivas",
                icon: Layers,
                desc: "Cliente sobe XLSX, sistema detecta abas + mapping + concept rules. Replace por janela (não duplica). OneDrive / Sharepoint / Google Sheets sync cada 1h via OAuth. Dicionário de campanhas extrai Produto + Fase + AdGroup dos nomes.",
                bullets: ["Modal preview antes/depois de cada upload", "Sync automático 1h em planilhas vivas", "Detector wide-format + regras de concept globais"],
              },
              {
                name: "Alertas, Tarefas & Auditoria",
                icon: Target,
                desc: "Squad gera alertas proativos com severidade e ação recomendada, integrados ao Painel. Inclui auditoria de tracking (GA4, GTM, data layer), SEO técnico e plano de correção priorizado.",
                bullets: ["Alertas com severidade + ação", "Estados (aberto, snooze, resolvido) + audit trail", "Auditoria SEO técnico + eventos GA4/GTM"],
              },
              {
                name: "Avaliação de Mídia (Beta)",
                icon: TrendingUp,
                desc: "Leitura executiva de como cada canal está performando: ROAS por canal, eficiência de atribuição, saturação por plataforma, anomalias e sugestão de mix. Pré-MTA: já mostra quando canais brigam pela mesma compra e quando o próximo R$ tem retorno marginal decrescente.",
                bullets: ["ROAS + CAC por canal semana atual vs anterior", "Saturação (curva Hill) + ponto de inflexão", "Eficiência de atribuição vs receita real (GA4)"],
              },
              {
                name: "Plano de Crescimento",
                icon: Target,
                desc: "Você fala a meta (R$ ou conversões) e o Seraik calcula o budget + 3 cenários (Conservador / Balanceado / Ambicioso) respeitando saturação MMM real, benchmark do segmento, plano semanal com capacity check, e alavancas omnichannel (CRM/SEO/WhatsApp/Loyalty) além de paid.",
                bullets: ["Reverso meta → budget com 3 cenários", "Saturação MMM + Benchmark + Weekly 4w + Omnichannel", "Histórico Planejado vs Realizado com tracking diário"],
              },
              {
                name: "Satélite de Marketing",
                icon: TrendingUp,
                desc: "Orientação contínua estilo GPS: rota atual em destaque + próximas 2-3 na fila, com horizonte temporal (AGORA / HOJE / ESTA SEMANA / ESTE MÊS) e tom imperativo. Você marca Feito / Pular / Lembrar. Pós-decisão tem follow-up 24h perguntando se o lift previsto rolou.",
                bullets: ["Sequência de rotas + check-in", "Follow-up 24h pós-decisão", "Recálculo automático quando contexto muda"],
              },
              {
                name: "Service Account Universal",
                icon: KeyRound,
                desc: "GA4 + Search Console + BigQuery via Service Account em vez de OAuth pessoal. Não expira, não depende de login individual, não precisa GCP OAuth verification. Tutorial 5 passos no /help/conexoes/ga4/service-account.",
                bullets: ["Sem expirações em 7 dias", "Não depende de pessoa (se sair, conexão segue)", "Audit trail separado de ações humanas"],
              },
            ].map((m) => (
              <div
                key={m.name}
                className="p-6 rounded-xl border border-violet-500/20 bg-violet-500/[0.03] hover:border-violet-500/40 hover:bg-violet-500/[0.06] transition-all space-y-4 seraik-chip-lift"
              >
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-lg bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                    <m.icon className="h-5 w-5 text-violet-300" />
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${
                    m.name.includes('(Beta)')
                      ? 'bg-amber-500/10 text-amber-300 border border-amber-500/30'
                      : 'bg-violet-500/10 text-violet-300 border border-violet-500/30'
                  }`}>
                    {m.name.includes('(Beta)') ? 'Beta' : 'Disponível'}
                  </span>
                </div>
                <h3 className="text-lg font-semibold">{m.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
                <ul className="space-y-1.5 text-sm text-muted-foreground/80">
                  {m.bullets.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-violet-400" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Separador visual + sub-header Em desenvolvimento */}
          <details className="mt-12 group">
            <summary className="mb-6 flex items-center gap-4 cursor-pointer list-none select-none">
              <div className="flex-1 h-px bg-border/40" />
              <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold inline-flex items-center gap-1.5 whitespace-nowrap">Em desenvolvimento <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" /> <span className="normal-case tracking-normal font-normal text-muted-foreground/60">clique para ver</span></p>
              <div className="flex-1 h-px bg-border/40" />
            </summary>

          {/* Em desenvolvimento — Beta 04.06.004 (10/06/2026): Ricardo flagou que
              Plano de Crescimento, Satelite e Autonomo ESTAO EM PRODUCAO (Beta
              04.00.000, 07/06/2026). Erro meu ter movido pra Em breve. Voltei
              pro Disponivel. Em breve fica com: Retail, Planning&CRM, MTA e
              Analise Colegiada (interno: Sprint VAR — NUNCA expor 'VAR' no
              produto, decisao Ricardo 11/06/2026). */}
          <div className="grid md:grid-cols-3 gap-5">
            {[
              {
                name: "Autônomo",
                icon: ShieldAlert,
                desc: "Seraik sugere, aprova em 2-step e registra em log imutável. Plano de mídia, alocação de budget e ajustes operacionais virám sugestões com 1-clique (reversível) ou código de 6 dígitos (sensível). Você executa na ferramenta original; Seraik audita tudo.",
                bullets: ["Sugestão automática com aprovação 2-step", "workspace_action_log imutável (auditável)", "Single click reversível, código pra sensível"],
              },
              {
                name: "Análise Colegiada",
                icon: BrainCircuit,
                desc: "Para decisões complexas (plano de mídia, alocação de budget, escalada de canal), múltiplos analistas com perfis diferentes geram visões independentes: um olha dados do cliente, outro olha dados de mercado, outro pondera perfil de risco. Um analista sênior arbitra e entrega o veredito, com as visões divergentes registradas.",
                bullets: ["3 perfis de análise independentes", "Sênior arbitra conforme perfil moderado/arriscado", "Auditoria das visões divergentes registrada"],
              },
              {
                name: "Atribuição multi-touch (MTA)",
                icon: BrainCircuit,
                desc: "Distribuição de crédito data-driven entre canais. Mostra co-assist, canibalismo e contribuição real além de last-click. Complementa o card Canais Brigando pelo Mesmo Caldo já disponível.",
                bullets: ["Modelo data-driven por janela", "Co-assist vs canibalismo", "Comparativo MTA vs Last-Click vs MMM"],
              },
              {
                name: "Retail",
                icon: Activity,
                desc: "Inteligência para sellers de marketplace. ROAS por SKU, Product Ads, ruptura. Mercado Livre via OAuth. Shopee, Amazon e VTEX em desenvolvimento.",
                bullets: ["Visão por produto: vendas × ROAS", "Campanhas Product Ads", "Alertas de estoque"],
              },
              {
                name: "Planning & CRM",
                icon: BrainCircuit,
                desc: "Planejamento de campanhas, réguas de lifecycle, orquestração de CRM. Mesas dedicadas para CRM e Sales operacional.",
                bullets: ["Réguas de ativação e retenção", "Calendário editorial sazonal", "Projeção de conversão"],
              },
            ].map((m) => (
              <div
                key={m.name}
                className="p-6 rounded-xl border border-border/40 bg-card/20 transition-colors space-y-4 opacity-85"
              >
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-lg bg-muted/40 flex items-center justify-center">
                    <m.icon className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider bg-muted/40 text-muted-foreground border border-border/40">
                    Em breve
                  </span>
                </div>
                <h3 className="text-lg font-semibold">{m.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{m.desc}</p>
                <ul className="space-y-1.5 text-sm text-muted-foreground/80">
                  {m.bullets.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0 mt-0.5 text-muted-foreground" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          </details>
        </div>
      </section>

      {/* ── Data Lakes & Big Data ────────────────────────────────────────────── */}
      <section id="data-lakes" className="border-t border-border/40 py-24">
        <div className="container max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs text-violet-300 uppercase tracking-widest mb-2 font-semibold">Data Lakes & Big Data</p>
              <h2 className="text-3xl font-bold mb-5 leading-tight">
                Seu Data Lake <span className="seraik-headline-gradient">respondendo perguntas de negócio</span>, em português.
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-4">
                A maioria das empresas tem terabytes de dados em <span className="text-foreground font-medium">BigQuery,
                Snowflake, Redshift ou Databricks</span>, e ainda assim continua tomando decisão por
                planilha. O motivo é simples: ninguém quer escrever SQL toda vez que precisa
                de uma resposta.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Seraik Insights conecta direto na sua camada de dados, entende o schema, traduz
                a pergunta em query e retorna análise pronta, com tendência, comparação e ação.
                <span className="text-foreground font-medium"> O Data Lake deixa de ser repositório e vira interlocutor.</span>
              </p>
              <div className="flex flex-wrap gap-2 mb-6">
                {[
                  { name: "BigQuery", available: true },
                  { name: "Google Sheets", available: true },
                  { name: "SFTP", available: true },
                  { name: "Snowflake", available: false },
                  { name: "Redshift", available: false },
                  { name: "Databricks", available: false },
                  { name: "PostgreSQL", available: false },
                ].map((s) => (
                  <span
                    key={s.name}
                    className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs ${
                      s.available
                        ? "border border-violet-500/40 bg-violet-500/10 text-violet-200"
                        : "border border-border/40 bg-muted/20 text-muted-foreground/60"
                    }`}
                  >
                    {s.name}
                    {!s.available && <span className="ml-1.5 text-[9px] opacity-70">em breve</span>}
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              {[
                // Beta 04.06.002: paleta violeta/indigo uniforme com 4 nuances
                {
                  title: "Query agêntica",
                  desc: "Sua pergunta em português vira SQL otimizado, executa no warehouse e volta com leitura executiva. Sem escrever uma linha de código.",
                  icon: BrainCircuit,
                  color: "border-violet-500/40 bg-violet-500/[0.07]",
                  accent: "text-violet-300",
                },
                {
                  title: "Multi-source aggregation",
                  desc: "Cruze receita do BigQuery com investimento do Google Ads e leads do CRM na mesma análise. O metric resolver decide a fonte com maior confiabilidade automaticamente.",
                  icon: Layers,
                  color: "border-violet-500/30 bg-violet-500/5",
                  accent: "text-violet-200",
                },
                {
                  title: "Volume não é problema",
                  desc: "Bilhões de eventos? Tabelas com 50M+ linhas? A agregação acontece no warehouse, não na sua máquina. O Squad só vê o resultado pronto.",
                  icon: Database,
                  color: "border-indigo-500/40 bg-indigo-500/[0.07]",
                  accent: "text-indigo-300",
                },
                {
                  title: "Cache inteligente",
                  desc: "Resultados ficam em cache curto por workspace para resposta instantânea, mas invalidam quando o dado muda. Velocidade de demo, frescor de produção.",
                  icon: Zap,
                  color: "border-indigo-500/30 bg-indigo-500/5",
                  accent: "text-indigo-200",
                },
              ].map((item) => (
                <div key={item.title} className={`p-4 rounded-xl border ${item.color} space-y-1.5`}>
                  <div className="flex items-center gap-2">
                    <item.icon className={`h-4 w-4 ${item.accent}`} />
                    <p className="text-sm font-semibold">{item.title}</p>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Área Autônoma destaque ────────────────────────────────────────────── */}
      <section id="autonomo" className="border-t border-border/40 py-24">
        <div className="container max-w-5xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-violet-400 uppercase tracking-widest">Diferencial de categoria</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted/40 text-muted-foreground border border-border/40 uppercase tracking-wider font-semibold">Em breve</span>
                </div>
                <h2 className="text-3xl font-bold mt-2">O SQUAD que age. Com sua aprovação.</h2>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Ferramentas de BI mostram o problema. O SQUAD resolve.
                José identifica anomalias na mídia, Lucas gera o plano de eventos, ambos sugerem
                a ação e esperam seu sinal verde antes de executar qualquer coisa. Estamos finalizando
                o gate de execução automática para ações pré-aprovadas. Em breve no produto.
              </p>
              <ul className="space-y-3">
                {[
                  { icon: Zap, text: "Auto-Tagging: envie a URL, receba o plano de eventos e a implementação pronta" },
                  { icon: Target, text: "Alertas proativos: ROAS crítico, budget > 80%, CPC acima do threshold" },
                  { icon: Shield, text: "Confirmation gate 90/10: o SQUAD sugere, você aprova nenhuma ação sem sua revisão" },
                  { icon: Activity, text: "Audit trail: registro de cada ação executada, por quem aprovou e qual foi o resultado" },
                ].map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-start gap-3">
                    <div className="h-6 w-6 rounded-md bg-violet-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon className="h-3.5 w-3.5 text-violet-400" />
                    </div>
                    <span className="text-sm text-muted-foreground">{text}</span>
                  </li>
                ))}
              </ul>
              <Button asChild variant="outline" className="gap-2 border-violet-500/40 text-violet-300 hover:bg-violet-500/10">
                <a href="#contato">
                  <FlaskConical className="h-4 w-4" /> Quero saber quando lançar
                </a>
              </Button>
            </div>

            <div className="space-y-3">
              {[
                // Beta 04.06.002: estados semanticos mas sem rainbow. Rose=alerta,
                // Violeta=decisao em foco, Emerald=concluido. 3 cores apenas.
                {
                  step: "01",
                  title: "José identifica o problema",
                  desc: "Campanha com ROAS crítico há 3 dias, budget quase esgotado. José já sabe o que pausar.",
                  color: "border-rose-500/30 bg-rose-500/5",
                  badgeText: "José · Alerta ativo",
                  badgeColor: "text-rose-300",
                  highlight: false,
                },
                {
                  step: "02",
                  title: "Pronto para executar. Você decide.",
                  desc: "A ação está preparada, com justificativa e impacto estimado. Um clique para aprovar, um clique para recusar.",
                  color: "border-violet-500/50 bg-violet-500/10 shadow-lg shadow-violet-900/20 seraik-glow-violet",
                  badgeText: "Aguardando sua decisão",
                  badgeColor: "text-violet-200 font-semibold",
                  highlight: true,
                },
                {
                  step: "03",
                  title: "Aprovado. Executado. Registrado.",
                  desc: "A ação acontece. O resultado fica no histórico. O SQUAD já monitora o próximo passo.",
                  color: "border-emerald-500/25 bg-emerald-500/5",
                  badgeText: "Concluído",
                  badgeColor: "text-emerald-300",
                  highlight: false,
                },
              ].map((item) => (
                <div key={item.step} className={`p-4 rounded-xl border ${item.color} space-y-1.5`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground font-mono">PASSO {item.step}</span>
                    <span className={`text-[10px] ${item.badgeColor}`}>{item.badgeText}</span>
                  </div>
                  <p className={`text-sm font-semibold ${item.highlight ? "text-violet-100" : ""}`}>{item.title}</p>
                  <p className="text-xs text-muted-foreground">{item.desc}</p>
                  {item.highlight && (
                    <div className="flex gap-2 pt-1">
                      <span className="text-[10px] border border-violet-400/40 text-violet-200 rounded px-2 py-0.5">Aprovar</span>
                      <span className="text-[10px] border border-border/30 text-muted-foreground/50 rounded px-2 py-0.5">Recusar</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Por que não Tableau / Power BI / ChatGPT ─────────────────────────── */}
      <section className="border-t border-border/40 py-24 bg-muted/10">
        <div className="container max-w-4xl">
          <div className="text-center mb-12">
            <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Comparativo</p>
            <h2 className="text-3xl font-bold">Por que não Tableau, Gemini ou Amplitude?</h2>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
              Cada ferramenta resolve uma parte do problema. Seraik Insights integra tudo e entrega decisão,
              não apenas dado. É a diferença entre ter um dashboard e ter um Squad de analistas.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/40">
                  <th className="text-left py-3 pr-4 text-muted-foreground font-medium">Funcionalidade</th>
                  <th className="text-center py-3 px-2 text-blue-400 font-semibold">Seraik Insights</th>
                  <th className="text-center py-3 px-2 text-muted-foreground font-medium text-xs">Tableau / Power BI</th>
                  <th className="text-center py-3 px-2 text-muted-foreground font-medium text-xs">ChatGPT / Gemini</th>
                  <th className="text-center py-3 px-2 text-muted-foreground font-medium text-xs">Amplitude / Insider</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISONS.map((row, i) => (
                  <tr key={row.feature} className={`border-b border-border/20 ${i % 2 === 0 ? "" : "bg-muted/10"}`}>
                    <td className="py-3 pr-4 text-muted-foreground">{row.feature}</td>
                    {[row.insights, row.bi, row.llm, row.analytics_ai].map((val, j) => (
                      <td key={j} className="py-3 px-3 text-center">
                        {val === true ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                        ) : val === false ? (
                          <X className="h-4 w-4 text-red-400/60 mx-auto" />
                        ) : (
                          <span className="text-[11px] text-muted-foreground">{val}</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-center text-xs text-muted-foreground mt-6">
            Tableau e Power BI são ferramentas de BI excelentes, mas exigem analista dedicado e setup complexo.
            Gemini e ChatGPT não têm seus dados. Amplitude e Insider cobrem analytics e ativação, mas não integram tudo numa decisão só.
            Seraik Insights conecta, analisa, recomenda e age.
          </p>
        </div>
      </section>

      {/* ── Como começar ───────────────────────────────────────────────────── */}
      <section id="planos" className="border-t border-border/40 py-24">
        <div className="container max-w-5xl">
          <div className="text-center mb-14">
            <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Como começar</p>
            <h2 className="text-3xl font-bold">Vamos montar o setup com você.</h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
              Cada cliente recebe um setup desenhado pra sua operação: time, fontes, profundidade
              e nível de ação. Conta pra gente o que precisa e a gente desenha o melhor caminho,
              do mais leve ao mais robusto.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-5">
            {CONTACT_TRACKS.map((track) => (
              <div key={track.name} className={`p-6 rounded-xl border ${track.border} space-y-4 flex flex-col`}>
                <div className={`h-10 w-10 rounded-lg bg-background/40 flex items-center justify-center`}>
                  <track.icon className={`h-5 w-5 ${track.accent}`} />
                </div>
                <h3 className="text-lg font-semibold">{track.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1">{track.description}</p>
                <Button asChild className="w-full" size="sm" variant="outline">
                  <a href="#contato">Falar com especialista <ArrowRight className="h-3.5 w-3.5 ml-1.5" /></a>
                </Button>
              </div>
            ))}
          </div>

          {/* Canais alternativos */}
          <div className="mt-10 rounded-2xl border border-border/30 bg-muted/10 p-6">
            <p className="text-xs text-muted-foreground uppercase tracking-widest text-center mb-5">Outros canais</p>
            <div className="grid md:grid-cols-3 gap-5 text-center">
              <div>
                <p className="text-sm font-semibold mb-1">Formulário</p>
                <p className="text-xs text-muted-foreground mb-2">Resposta em até 1 dia útil</p>
                <a href="#contato" className="text-xs text-violet-400 hover:text-violet-300 transition-colors">Preencher formulário →</a>
              </div>
              <div>
                <p className="text-sm font-semibold mb-1">E-mail</p>
                <p className="text-xs text-muted-foreground mb-2">Time comercial direto</p>
                <a href="mailto:negocios@seraik.com" className="text-xs text-violet-400 hover:text-violet-300 transition-colors">negocios@seraik.com</a>
              </div>
              <div>
                <p className="text-sm font-semibold mb-1">WhatsApp</p>
                <p className="text-xs text-muted-foreground mb-2">+55 11 96315-0786</p>
                <a href="https://wa.me/5511963150786" target="_blank" rel="noopener noreferrer" className="text-xs text-violet-400 hover:text-violet-300 transition-colors">Abrir conversa →</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stack Estratégico ─────────────────────────────────────────────────── */}
      <section className="border-t border-border/40 py-16 bg-muted/10">
        <div className="container max-w-4xl">
          <div className="text-center mb-10">
            <p className="text-xs text-muted-foreground uppercase tracking-widest mb-2">Ecossistema aunica</p>
            <h3 className="text-xl font-bold">Parte de um stack estratégico completo</h3>
            <p className="text-sm text-muted-foreground mt-2">Seraik Insights é a camada de Decisão, parte de uma tríade de produtos aunica.</p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {[
              { name: "mydata.io", role: "Dados", desc: "CDP + coleta + pipeline", color: "border-border/60 text-muted-foreground" },
              { name: "Seraik Insights", role: "Decisão", desc: "Analytics + Insights + Ação", color: "border-blue-500/50 text-blue-400 bg-blue-500/5" },
              { name: "Orquestra", role: "Ativação", desc: "CRM + jornadas + réguas", color: "border-border/60 text-muted-foreground" },
            ].map((item, i) => (
              <div key={item.name} className="flex items-center gap-4">
                <div className={`px-6 py-4 rounded-xl border text-center space-y-1 ${item.color}`}>
                  <p className={`font-bold ${item.color.includes("blue") ? "text-blue-400" : ""}`}>{item.name}</p>
                  <p className="text-xs font-medium text-muted-foreground">{item.role}</p>
                  <p className="text-[10px] text-muted-foreground/60">{item.desc}</p>
                </div>
                {i < 2 && <ArrowRight className="h-4 w-4 text-muted-foreground/40 flex-shrink-0 hidden sm:block" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Cases reais por segmento ───────────────────────────────────────── */}
      <section id="cases" className="border-t border-border/40 py-24">
        <div className="container max-w-5xl">
          <div className="text-center mb-14">
            <p className="text-xs text-violet-300 uppercase tracking-widest mb-2 font-semibold">Cases reais</p>
            <h2 className="text-3xl font-bold leading-tight">Onde a operação <span className="text-blue-400">já está rodando.</span></h2>
            <p className="text-muted-foreground mt-3 max-w-2xl mx-auto">
              Problemas reais de operações reais, resolvidos em segundos pelo Squad. Setores, contextos
              e métricas concretas. Os nomes ficam protegidos por NDA, os números não.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {[
              {
                segment: "Pharma & Saúde",
                icon: Briefcase,
                problem: "Diretoria pedia foto executiva diária; analytics levava 3 dias para consolidar GA4 + Ads + planilha de leads do CRM.",
                action: "Painel Executivo configurado por segmento Lead Gen, com prioridade Spreadsheet → CRM → Analytics. Squad consolida tudo em uma tela.",
                metric: "3 dias → 2 min",
                result: "Reuniões de diretoria com dado fresco do dia.",
              },
              {
                segment: "Imobiliário & Incorporadora",
                icon: Building2,
                problem: "Incorporadora com lançamentos simultâneos, sem visibilidade de funil por fase (LANÇAMENTO/ESTOQUE/BIOS) nem comparativo entre empreendimentos.",
                action: "Funil customizável por segmento imobiliário, filtros por Produto e Fase, mídia consolidada por empreendimento. Squad cruza CAC, conversão e velocidade de venda.",
                metric: "CAC -22%",
                result: "Realocação entre fases. Lançamento ganha investimento, estoque sai do automático.",
              },
              {
                segment: "Energia & Combustíveis",
                icon: Database,
                problem: "Holding com 6 unidades de negócio e dados em BigQuery não-padronizado. Cada head puxava relatório do jeito próprio.",
                action: "Workspaces isolados por unidade com Painel padronizado. Metric resolver consolida no nível de holding.",
                metric: "1 linguagem · 6 unidades",
                result: "Comitê semanal vira diário. Decisões deixam de esperar planilha.",
              },
              {
                segment: "SaaS & Lead Gen",
                icon: BrainCircuit,
                problem: "Time de growth com 2 analistas para 6 produtos, sempre atrasado em diagnóstico de queda de funil e reativação de leads frios.",
                action: "Mesas de Performance por produto. Sofia identifica padrão, José revisa mídia, Lucas valida tracking.",
                metric: "4 dias → 30 min",
                result: "Time opera com profundidade analítica de squad sênior, sem contratar.",
              },
              {
                segment: "Agência multi-cliente",
                icon: Layers,
                problem: "Agência rodando 14 clientes, cada gerente com seu Looker, sem padronização, contexto se perdia entre handovers.",
                action: "1 organização → 14 workspaces isolados, mesma estrutura. Memória de contexto persistente. Handover sem perda.",
                metric: "2 sem → 2 dias",
                result: "Gerentes operam 4-5 contas com profundidade que antes davam para 2.",
              },
              {
                segment: "Fintech & Regulado",
                icon: Shield,
                problem: "Operação sob BACEN/LGPD: dados sensíveis não podiam sair do ambiente interno. BI tradicional exigia time grande.",
                action: "Deploy On-Premise via Docker no ambiente do cliente. Squad opera dentro da rede, dados nunca saem.",
                metric: "Equipe 6 → 2",
                result: "Compliance mantida. Squad assume trabalho repetitivo, time foca em estratégia.",
              },
            ].map((c) => (
              <div key={c.segment} className="p-6 rounded-xl border border-border/40 bg-card/40 hover:border-border/60 transition-colors space-y-5">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-muted/40 flex items-center justify-center">
                    <c.icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="font-semibold text-base">{c.segment}</p>
                </div>

                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.problem}</p>
                  <p className="text-sm text-muted-foreground/80 leading-relaxed">{c.action}</p>
                </div>

                <div className="pt-4 border-t border-border/30">
                  <p className="text-2xl font-bold text-blue-400 leading-none mb-1.5">{c.metric}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{c.result}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 p-5 rounded-xl border border-border/40 bg-muted/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center sm:text-left">
              Quer ver um case detalhado do seu segmento? <span className="text-foreground font-medium">Pedimos NDA, mostramos números reais.</span>
            </p>
            <a
              href="#contato"
              className="shrink-0 inline-flex items-center gap-2 rounded-lg border border-blue-500/40 bg-blue-500/10 text-blue-300 px-4 py-2 text-sm font-medium hover:bg-blue-500/20 transition-colors whitespace-nowrap"
            >
              Pedir case do meu segmento <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </div>
        </div>
      </section>

      <FAQSection />

      {/* ── CTA final ────────────────────────────────────────────────────────── */}
      <section className="border-t border-border/40 py-20">
        <div className="container max-w-2xl text-center space-y-6">
          <h2 className="text-3xl font-bold">O Squad está pronto. E você?</h2>
          <p className="text-muted-foreground leading-relaxed">
            Conecte suas fontes em minutos. Sem SQL, sem BI developer, sem esperar relatório.
            Faça sua primeira pergunta em linguagem de negócio e receba análise, com ação, em segundos.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild size="lg" className="text-base gap-2">
              <a href="#contato">Falar com especialista <ArrowRight className="h-4 w-4" /></a>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-base gap-2">
              <a href={ONE_PAGER_URL} target="_blank" rel="noopener noreferrer">
                <Download className="h-4 w-4" /> Baixar one-pager
              </a>
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">Retorno em até 1 dia útil · Sem compromisso</p>
        </div>
      </section>

      {/* ── Formulário de Contato ──────────────────────────────────────────────── */}
      <ContactSection />

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer className="border-t border-border/40 bg-muted/20 py-14">
        <div className="container max-w-5xl">
          <div className="grid md:grid-cols-4 gap-10 mb-10">

            {/* Marca */}
            <div className="md:col-span-2 space-y-4">
              <div className="flex items-center">
                {/* Beta 04.03.005 (10/06/2026): logo COLORIDO sempre, footer. */}
                <img
                  src="/logos/seraik-insights/seraik-insights-color.svg"
                  alt="Seraik Insights"
                  className="h-12 w-auto block select-none"
                  draggable={false}
                />
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">
                A plataforma que transforma dados em decisão: em tempo real, em português,
                com ação recomendada. Squad de Analistas 24/7 conectado às suas fontes reais.
              </p>
              {/* Beta 04.06.x (11/06/2026, Ricardo P0): logo aunica antigo
                  (PNG fundo escuro) nao renderizava bem em light mode.
                  Removido. aunica labs ja cobre marca corporativa. */}
              <div className="flex items-center gap-3 pt-2">
                <span className="text-xs text-muted-foreground/60">Por</span>
                <Image
                  src={AUNICALABS_LOGO}
                  alt="aunica labs"
                  width={88}
                  height={20}
                  className="h-4 w-auto object-contain opacity-80 dark:opacity-100"
                  unoptimized
                />
              </div>
            </div>

            {/* Navegação */}
            <div className="space-y-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Plataforma</p>
              <div className="flex flex-col gap-2 text-sm">
                <Link href="#o-que-e" className="text-muted-foreground hover:text-foreground transition-colors">O que é</Link>
                <Link href="#como-funciona" className="text-muted-foreground hover:text-foreground transition-colors">Módulos</Link>
                <Link href="#data-lakes" className="text-muted-foreground hover:text-foreground transition-colors">Data Lakes</Link>
                <Link href="#squad" className="text-muted-foreground hover:text-foreground transition-colors">Squad</Link>
                <Link href="#cases" className="text-muted-foreground hover:text-foreground transition-colors">Cases</Link>
              </div>
            </div>

            {/* Contato */}
            <div className="space-y-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Contato</p>
              <div className="flex flex-col gap-2 text-sm">
                <Link href="/login" className="text-muted-foreground hover:text-foreground transition-colors">Entrar na plataforma</Link>
                <Link href="#planos" className="text-muted-foreground hover:text-foreground transition-colors">Como começar</Link>
                <a href="mailto:negocios@seraik.com" className="text-muted-foreground hover:text-foreground transition-colors">negocios@seraik.com</a>
                <a href="https://wa.me/5511963150786" target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">+55 11 96315-0786</a>
                <a href={ONE_PAGER_URL} target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:text-violet-300 transition-colors inline-flex items-center gap-1">
                  <Download className="h-3 w-3" /> Baixar one-pager
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-border/40 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground/60">
            <p>© 2026 aunica labs · todos os direitos reservados</p>
            <p>seraik.com</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
